<template>
    <div class="users-page">
        <Toast />
        <ConfirmDialog />

        <div class="users-page__header">
            <h4 class="users-page__title">Brukere</h4>
        </div>

        <DataTable
            :value="users"
            :loading="loading"
            dataKey="id"
            stripedRows
            paginator
            :rows="20"
            :rowsPerPageOptions="[20, 50, 100]"
            filterDisplay="menu"
            v-model:filters="filters"
            :globalFilterFields="['name', 'email', 'location_name']"
            sortMode="single"
            removableSort
        >
            <template #header>
                <div class="users-page__toolbar">
                    <IconField class="users-page__search">
                        <InputIcon class="pi pi-search" />
                        <InputText v-model="filters.global.value" placeholder="Søk" style="width: 100%" />
                    </IconField>
                    <Button label="Eksporter" icon="pi pi-file-excel" outlined @click="exportUsers" />
                </div>
            </template>

            <Column field="name" header="Navn" sortable :sortField="norwegianSortField('name')" filter filterPlaceholder="Søk etter navn">
                <template #body="{ data }">
                    <button type="button" class="users-page__name-link" @click="openEditDialog(data)">
                        {{ data.name }}
                    </button>
                </template>
            </Column>

            <Column field="email" header="E-post" sortable :sortField="norwegianSortField('email')" filter filterPlaceholder="Søk etter e-post" />

            <Column field="location_name" header="Kommune" sortable :sortField="norwegianSortField('location_name')" filterField="location_name" :showFilterMatchModes="false" style="min-width: 12rem">
                <template #filter="{ filterModel, filterCallback }">
                    <MultiSelect
                        v-model="filterModel.value"
                        :options="locationOptions"
                        placeholder="Alle"
                        display="chip"
                        filter
                        @change="filterCallback()"
                    />
                </template>
            </Column>

            <Column field="is_anonymous" header="Anonym" sortable filterField="is_anonymous" :showFilterMatchModes="false" style="width: 6rem">
                <template #body="{ data }">
                    <i v-if="data.is_anonymous" class="pi pi-eye-slash" aria-label="Anonym"></i>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Select
                        v-model="filterModel.value"
                        :options="boolOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Alle"
                        showClear
                        @change="filterCallback()"
                    />
                </template>
            </Column>

            <Column field="is_admin" header="Admin" sortable filterField="is_admin" :showFilterMatchModes="false" style="width: 6rem">
                <template #body="{ data }">
                    <i v-if="data.is_admin" class="pi pi-user" aria-label="Admin"></i>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Select
                        v-model="filterModel.value"
                        :options="boolOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Alle"
                        showClear
                        @change="filterCallback()"
                    />
                </template>
            </Column>

            <template #empty>
                Ingen brukere funnet.
            </template>
        </DataTable>

        <UserEditDialog
            v-model:visible="showEditDialog"
            :user="editingUser"
            :municipalities="municipalities"
            :all-groups="allGroups"
            @saved="syncUserInList"
        />
    </div>
</template>
<script>
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import ConfirmDialog from 'primevue/confirmdialog';
import { FilterMatchMode } from '@primevue/core/api';
import UserEditDialog from './UserEditDialog';

// PrimeVue's DataTable sorts using the runtime's default Intl.Collator locale,
// which may not be Norwegian and wouldn't place æ/ø/å after z the Norwegian way.
// Remapping them to codepoints above 'z' keeps that ordering under any comparator.
function norwegianSortKey(value) {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value)
        .toLowerCase()
        .replaceAll('æ', '')
        .replaceAll('ø', '')
        .replaceAll('å', '');
}

export default {
    components: {
        DataTable,
        Column,
        InputText,
        IconField,
        InputIcon,
        Select,
        MultiSelect,
        Button,
        Toast,
        ConfirmDialog,
        UserEditDialog,
    },
    data() {
        return {
            users: [],
            municipalities: [],
            allGroups: [],
            loading: true,
            boolOptions: [
                { label: 'Ja', value: true },
                { label: 'Nei', value: false },
            ],
            filters: {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
                name: { value: null, matchMode: FilterMatchMode.CONTAINS },
                email: { value: null, matchMode: FilterMatchMode.CONTAINS },
                location_name: { value: null, matchMode: FilterMatchMode.IN },
                is_admin: { value: null, matchMode: FilterMatchMode.EQUALS },
                is_anonymous: { value: null, matchMode: FilterMatchMode.EQUALS },
            },
            showEditDialog: false,
            editingUser: null,
        };
    },
    computed: {
        locationOptions() {
            return [...new Set(this.users.map(user => user.location_name).filter(Boolean))]
                .sort((a, b) => a.localeCompare(b, 'nb'));
        },
    },
    mounted() {
        this.fetchUsers();
        this.fetchMunicipalities();
        this.fetchGroups();
    },
    methods: {
        norwegianSortField(field) {
            return item => norwegianSortKey(item[field]);
        },
        async fetchUsers() {
            try {
                const response = await fetch('/api/v1/user');
                this.users = await response.json();
            } catch (error) {
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        async fetchMunicipalities() {
            try {
                const response = await fetch('/fetch/municipalities');
                this.municipalities = await response.json();
            } catch (error) {
                console.error(error);
            }
        },
        async fetchGroups() {
            try {
                const response = await fetch('/api/v1/usergroup');
                this.allGroups = await response.json();
            } catch (error) {
                console.error(error);
            }
        },
        exportUsers() {
            window.location.href = '/api/v1/user/export';
        },
        openEditDialog(user) {
            this.editingUser = user;
            this.showEditDialog = true;
        },
        syncUserInList(user) {
            const index = this.users.findIndex(u => u.id === user.id);
            if (index !== -1) {
                this.users.splice(index, 1, user);
            }
        },
    },
};
</script>
<style scoped>
.users-page__header {
    margin-bottom: 1rem;
}

.users-page__title {
    margin: 0;
}

.users-page__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.users-page__search {
    max-width: 320px;
}

.users-page__name-link {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: var(--p-primary-color, #3b82f6);
    cursor: pointer;
    text-decoration: none;
}

.users-page__name-link:hover {
    text-decoration: underline;
}
</style>
